<template>
    <template v-if="!isAGroup">
        <WalletTableSingleRow
            :key="group.id"
            :card="group.cards[0]"
            :index="index"
            @edit="handleEdit"
            @delete="handleDelete"
            :gridStyle="gridStyle"
            @view-details="handleViewDetails"
            :totalCards="totalCards"
        />
    </template>
    <div v-else class="w-full overflow-visible group">
        <header
            class="flex items-stretch text-sm w-full border-slate-200 dark:border-slate-700 whitespace-nowrap relative cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700"
            :class="{
                'border-b': !isExpanded && index !== totalCards - 1,
            }"
            @click="toggleExpand"
        >
            <!-- Card info section -->
            <div
                class="flex gap-4 items-center sticky left-0 pl-6 bg-white dark:bg-slate-800 h-full self-stretch py-3 group-hover:bg-slate-50 dark:group-hover:bg-slate-700"
            >
                <!-- Card image -->
                <div class="relative shrink-0">
                    <ImageLoader>
                        <template #image>
                            <div class="card-stack">
                                <div
                                    class="absolute w-[80%] h-full rounded-lg bg-slate-200 dark:bg-slate-800 -bottom-1.5 left-1/2 z-0 -translate-x-1/2"
                                ></div>
                                <div
                                    class="absolute w-[90%] h-full rounded-lg bg-slate-300 dark:bg-slate-700 -bottom-1 left-1/2 z-10 -translate-x-1/2"
                                ></div>
                                <img
                                    :src="`${cdnUrl}/${group.cards[0]?.card?.card_image?.path}/_small/${group.cards[0]?.card?.card_image?.file_name}`"
                                    class="max-w-[80px] lg:max-w-[80px] h-auto relative z-20"
                                    alt="Card image"
                                />
                            </div>
                        </template>
                        <template #error>
                            <div
                                class="w-20 h-12 bg-slate-100 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700 flex items-center justify-center"
                            >
                                <WalletIcon
                                    icon="unmatched-card"
                                    class="h-6 w-6"
                                />
                            </div>
                        </template>
                    </ImageLoader>

                    <!-- Card count badge -->
                    <div
                        v-if="isAGroup"
                        class="absolute -right-2 -bottom-2 bg-slate-900 text-white text-xs font-medium rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center z-30"
                    >
                        {{ numberOfCards }}
                    </div>
                </div>

                <!-- Card title -->
                <h3
                    class="font-medium text-slate-800 dark:text-slate-100 truncate min-w-72"
                    v-if="group.cards[0]?.card_id"
                >
                    {{ group.cards[0]?.card_title }}
                </h3>
                <h3 class="font-medium text-slate-800 dark:text-slate-100 truncate min-w-72" v-else>
                    {{ isAGroup ? 'Unknown cards' : 'Unknown card' }}
                </h3>
            </div>

            <div class="grow"></div>

            <!-- Expand/collapse button -->
            <div class="sticky right-0 p-3">
                <button
                    @click.stop="toggleExpand"
                    class="bg-white dark:bg-slate-700 rounded-full h-8 w-8 inline-flex items-center justify-center shrink-0 ml-auto z-10 hover:bg-slate-50 dark:hover:bg-slate-600 hover:shadow dark:shadow-slate-900/50 transition-all duration-200 my-3"
                    :aria-expanded="isExpanded"
                    :aria-label="
                        isExpanded
                            ? 'Collapse card details'
                            : 'Expand card details'
                    "
                >
                    <Transition name="fade" mode="out-in">
                        <Icon
                            :icon="isExpanded ? 'chevron-up' : 'chevron-down'"
                            class="h-5 w-5"
                        />
                    </Transition>
                </button>
            </div>
        </header>
    </div>
    <div
        v-if="isAGroup"
        ref="expandedContent"
        class="transition-all duration-300 ease-in-out"
        :class="{ hidden: !isExpanded }"
        :style="{
            maxHeight: isExpanded ? expandedHeight : '0px',
            opacity: isExpanded ? 1 : 0,
        }"
    >
        <div class="relative flex gap-4" v-for="(card, i) in group.cards">
            <WalletTableSingleRow
                :key="card.id"
                :card="card"
                :index="i"
                :gridStyle="adjustedGridStyle"
                :isAGroup="isAGroup"
                @edit="handleEdit"
                @delete="handleDelete"
                @view-details="handleViewDetails"
                :totalCards="totalCards"
            />
        </div>
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import WalletIcon from '@/components/wallet/wallet-icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import WalletTableSingleRow from '@/components/wallet/wallet-table-single-row.vue';
import { useWalletStore } from '@/store/wallet';
import { storeToRefs } from 'pinia';
import { ref, computed, watch, nextTick } from 'vue';
const props = defineProps({
    group: {
        type: Object,
        required: true,
    },
    gridStyle: {
        type: Object,
        required: true,
    },
    index: {
        type: Number,
        default: 0,
    },
    totalCards: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits(['edit', 'delete', 'view-details']);
const walletStore = useWalletStore();
const cdnUrl = ref(window.cdnUrl);
const isExpanded = ref(false);

const adjustedGridStyle = computed(() => {
    if (!props.gridStyle || !props.gridStyle.gridTemplateColumns) {
        return props.gridStyle;
    }

    const columnsString = props.gridStyle.gridTemplateColumns;
    const columns = columnsString.split('  ');

    if (columns.length > 0) {
        const pxMatch = columns[0].match(/(\d+)px/);
        if (pxMatch) {
            columns[0] = '272px';
        }
    }

    return {
        gridTemplateColumns: columns.join('  '),
    };
});
const expandedContent = ref(null);
const expandedHeight = ref('auto');

const { groupSameCards } = storeToRefs(walletStore);

const toggleExpand = () => {
    isExpanded.value = !isExpanded.value;
};
const numberOfCards = computed(() => {
    return props.group?.cards?.length || 0;
});

const isAGroup = computed(() => {
    return groupSameCards.value && numberOfCards.value > 1;
});

const handleEdit = (card) => {
    emit('edit', card);
};
const handleDelete = (card) => {
    emit('delete', card);
};
const handleViewDetails = (card) => {
    emit('view-details', card);
};

watch([isExpanded, () => props.group?.cards?.length], async (newVal) => {
    if (isExpanded.value) {
        expandedHeight.value = 'auto';
        await nextTick();
        if (expandedContent.value) {
            const height = expandedContent.value.scrollHeight;

            expandedHeight.value = height + 20 + 'px';
        }
    }
});
</script>

<style scoped>
/* Animation transitions */
.expand-enter-active,
.expand-leave-active {
    transition: all 0.3s ease;
    overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
    opacity: 0;
    max-height: 0 !important;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.cards-stacked::before,
.cards-stacked::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 8px;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    z-index: -1;
}

.cards-stacked::before {
    bottom: -4px;
    left: 2px;
    transform: rotate(-2deg);
}

.cards-stacked::after {
    bottom: -8px;
    left: 0;
    transform: rotate(-4deg);
}

.card-stack {
    position: relative;
    display: inline-block;
}
</style>
